import * as React from 'react';

import { enumUnit, Unit } from '@rematter/frontend-lib-api-client';
import { Button, ConsoleDialog, NumberField, Select } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useSetAtom } from 'jotai';
import { Check } from 'lucide-react';

import { completeWorkOrderWithActualsAtom } from '../../_graphql/mutationWorkOrder';
import { useTranslations } from '../../i18n/useTranslations';
import {
  WorkOrderInputItemRecord,
  WorkOrderOutputItemRecord,
} from '../../types/workOrder';

import { SelectInventoryLot } from '~/shared/components/select-fields/SelectInventoryLot';
import { SelectInventoryTag } from '~/shared/components/select-fields/SelectInventoryTag';

type CompletionInputItem = {
  inputItemId: string;
  itemId: string;
  itemName: string;
  plannedQuantity: number;
  plannedQuantityUnit: Unit;
  actualQuantity: number | null;
  actualQuantityUnit: Unit;
  lotId?: string | null;
  tagId?: string | null;
};

type CompletionOutputItem = {
  outputItemId: string;
  itemId: string;
  itemName: string;
  expectedQuantity: number;
  expectedQuantityUnit: Unit;
  actualQuantity: number | null;
  actualQuantityUnit: Unit;
  targetLotId?: string | null;
  targetTagId?: string | null;
};

type WorkOrderCompletionDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  workOrderId: string;
  facilityId: string;
  inputItems: WorkOrderInputItemRecord[];
  outputItems: WorkOrderOutputItemRecord[];
};

export const WorkOrderCompletionDialog = ({
  isOpen,
  onClose,
  onSuccess,
  workOrderId,
  facilityId,
  inputItems,
  outputItems,
}: WorkOrderCompletionDialogProps) => {
  const t = useTranslations();
  const toast = useToast();

  const [completionInputs, setCompletionInputs] = React.useState<
    CompletionInputItem[]
  >([]);
  const [completionOutputs, setCompletionOutputs] = React.useState<
    CompletionOutputItem[]
  >([]);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const completeWorkOrder = useSetAtom(completeWorkOrderWithActualsAtom({}));

  React.useEffect(() => {
    if (isOpen) {
      setCompletionInputs(
        inputItems.map((item) => ({
          inputItemId: item.id,
          itemId: item.itemId,
          itemName: item.itemName || item.itemId,
          plannedQuantity: item.plannedQuantityValue,
          plannedQuantityUnit: item.plannedQuantityUnit as Unit,
          actualQuantity: item.actualQuantityValue ?? item.plannedQuantityValue,
          actualQuantityUnit: (item.actualQuantityUnit ||
            item.plannedQuantityUnit) as Unit,
          lotId: item.lotId,
          tagId: item.tagId,
        })),
      );

      setCompletionOutputs(
        outputItems.map((item) => ({
          outputItemId: item.id,
          itemId: item.itemId,
          itemName: item.itemName || item.itemId,
          expectedQuantity: item.expectedQuantityValue,
          expectedQuantityUnit: item.expectedQuantityUnit as Unit,
          actualQuantity:
            item.actualQuantityValue ?? item.expectedQuantityValue,
          actualQuantityUnit: (item.actualQuantityUnit ||
            item.expectedQuantityUnit) as Unit,
          targetLotId: item.targetLotId,
          targetTagId: item.targetTagId,
        })),
      );
    }
  }, [isOpen, inputItems, outputItems]);

  const handleClose = React.useCallback(() => {
    setCompletionInputs([]);
    setCompletionOutputs([]);
    onClose();
  }, [onClose]);

  const handleUpdateInputActual = React.useCallback(
    (index: number, actualQuantity: number | null) => {
      setCompletionInputs((prev) =>
        prev.map((item, i) =>
          i === index ? { ...item, actualQuantity } : item,
        ),
      );
    },
    [],
  );

  const handleUpdateInputUnit = React.useCallback(
    (index: number, actualQuantityUnit: Unit) => {
      setCompletionInputs((prev) =>
        prev.map((item, i) =>
          i === index ? { ...item, actualQuantityUnit } : item,
        ),
      );
    },
    [],
  );

  const handleUpdateOutputActual = React.useCallback(
    (index: number, actualQuantity: number | null) => {
      setCompletionOutputs((prev) =>
        prev.map((item, i) =>
          i === index ? { ...item, actualQuantity } : item,
        ),
      );
    },
    [],
  );

  const handleUpdateOutputUnit = React.useCallback(
    (index: number, actualQuantityUnit: Unit) => {
      setCompletionOutputs((prev) =>
        prev.map((item, i) =>
          i === index ? { ...item, actualQuantityUnit } : item,
        ),
      );
    },
    [],
  );

  const handleUpdateInputLot = React.useCallback(
    (index: number, lotId: string | undefined) => {
      setCompletionInputs((prev) =>
        prev.map((item, i) =>
          i === index ? { ...item, lotId: lotId || null } : item,
        ),
      );
    },
    [],
  );

  const handleUpdateInputTag = React.useCallback(
    (index: number, tagId: string | undefined) => {
      setCompletionInputs((prev) =>
        prev.map((item, i) =>
          i === index ? { ...item, tagId: tagId || null } : item,
        ),
      );
    },
    [],
  );

  const handleUpdateOutputLot = React.useCallback(
    (index: number, targetLotId: string | undefined) => {
      setCompletionOutputs((prev) =>
        prev.map((item, i) =>
          i === index ? { ...item, targetLotId: targetLotId || null } : item,
        ),
      );
    },
    [],
  );

  const handleUpdateOutputTag = React.useCallback(
    (index: number, targetTagId: string | undefined) => {
      setCompletionOutputs((prev) =>
        prev.map((item, i) =>
          i === index ? { ...item, targetTagId: targetTagId || null } : item,
        ),
      );
    },
    [],
  );

  const handleSubmit = React.useCallback(async () => {
    const hasInvalidInputs = completionInputs.some(
      (item) => item.actualQuantity === null || item.actualQuantity < 0,
    );
    const hasInvalidOutputs = completionOutputs.some(
      (item) => item.actualQuantity === null || item.actualQuantity < 0,
    );

    if (hasInvalidInputs || hasInvalidOutputs) {
      toast.error(
        t.workOrderDetailView.completionDialog.validation
          .actualQuantityRequired,
      );
      return;
    }

    try {
      setIsSubmitting(true);

      await completeWorkOrder({
        id: workOrderId,
        actualInputs: completionInputs.map((item) => ({
          inputItemId: item.inputItemId,
          actualQuantity: item.actualQuantity ?? 0,
          actualQuantityUnit: item.actualQuantityUnit,
          lotId: item.lotId,
          tagId: item.tagId,
        })),
        actualOutputs: completionOutputs.map((item) => ({
          outputItemId: item.outputItemId,
          actualQuantity: item.actualQuantity ?? 0,
          actualQuantityUnit: item.actualQuantityUnit,
          targetLotId: item.targetLotId,
          targetTagId: item.targetTagId,
        })),
      });

      toast.success(t.workOrderDetailView.completionDialog.completeSuccess);
      onSuccess?.();
      handleClose();
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'An error occurred';
      toast.error(
        t.workOrderDetailView.completionDialog.completeError(errorMessage),
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [
    completionInputs,
    completionOutputs,
    workOrderId,
    completeWorkOrder,
    toast,
    t,
    onSuccess,
    handleClose,
  ]);

  const unitOptions = [
    { key: enumUnit.Pounds, label: 'LB' },
    { key: enumUnit.Kilograms, label: 'KG' },
    { key: enumUnit.MetricTons, label: 'MT' },
    { key: enumUnit.ShortTons, label: 'ST' },
    { key: enumUnit.Each, label: 'EA' },
  ];

  const canSubmit =
    !isSubmitting &&
    completionInputs.every(
      (item) => item.actualQuantity !== null && item.actualQuantity >= 0,
    ) &&
    completionOutputs.every(
      (item) => item.actualQuantity !== null && item.actualQuantity >= 0,
    );

  return (
    <ConsoleDialog
      className="@WorkOrderCompletionDialog"
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
      title={t.workOrderDetailView.completionDialog.title}
      content={
        <div className="max-h-[70vh] space-y-6 overflow-y-auto">
          <p className="text-sm text-neutral/60">
            {t.workOrderDetailView.completionDialog.subtitle}
          </p>

          {completionInputs.length > 0 && (
            <div className="space-y-3">
              <h3 className="font-medium">
                {t.workOrderDetailView.completionDialog.inputItemsTitle}
              </h3>
              <div className="space-y-2">
                <div className="grid grid-cols-12 gap-2 text-xs font-medium text-neutral/60">
                  <div className="col-span-2">
                    {t.workOrderDetailView.completionDialog.itemColumn}
                  </div>
                  <div className="col-span-2">
                    {t.workOrderDetailView.completionDialog.lotColumn}
                  </div>
                  <div className="col-span-2">
                    {t.workOrderDetailView.completionDialog.tagColumn}
                  </div>
                  <div className="col-span-2">
                    {t.workOrderDetailView.completionDialog.plannedColumn}
                  </div>
                  <div className="col-span-2">
                    {t.workOrderDetailView.completionDialog.actualColumn}
                  </div>
                  <div className="col-span-2">
                    {t.workOrderDetailView.completionDialog.unitColumn}
                  </div>
                </div>
                {completionInputs.map((item, index) => (
                  <div
                    key={item.inputItemId}
                    className="grid grid-cols-12 items-center gap-2 rounded border border-neutral/20 p-2"
                  >
                    <div className="col-span-2 truncate text-sm">
                      {item.itemName}
                    </div>
                    <div className="col-span-2">
                      <SelectInventoryLot
                        placeholder={
                          t.workOrderDetailView.completionDialog.selectLot
                        }
                        value={item.lotId || undefined}
                        onChange={(lotId) => handleUpdateInputLot(index, lotId)}
                        facilityId={facilityId}
                        isDisabled={isSubmitting}
                      />
                    </div>
                    <div className="col-span-2">
                      <SelectInventoryTag
                        placeholder={
                          t.workOrderDetailView.completionDialog.selectTag
                        }
                        value={item.tagId || undefined}
                        onChange={(tagId) => handleUpdateInputTag(index, tagId)}
                        facilityId={facilityId}
                        itemId={item.itemId}
                        lotId={item.lotId || undefined}
                        isDisabled={isSubmitting}
                      />
                    </div>
                    <div className="col-span-2 text-sm text-neutral/60">
                      {item.plannedQuantity.toLocaleString()}{' '}
                      {item.plannedQuantityUnit}
                    </div>
                    <div className="col-span-2">
                      <NumberField
                        value={item.actualQuantity ?? undefined}
                        onChange={(val) =>
                          handleUpdateInputActual(index, val ?? null)
                        }
                        minValue={0}
                        isDisabled={isSubmitting}
                        aria-label="Actual quantity"
                      />
                    </div>
                    <div className="col-span-2">
                      <Select
                        value={item.actualQuantityUnit}
                        onChange={(key) =>
                          handleUpdateInputUnit(index, key as Unit)
                        }
                        options={unitOptions}
                        isDisabled={isSubmitting}
                        aria-label="Unit"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {completionOutputs.length > 0 && (
            <div className="space-y-3">
              <h3 className="font-medium">
                {t.workOrderDetailView.completionDialog.outputItemsTitle}
              </h3>
              <div className="space-y-2">
                <div className="grid grid-cols-12 gap-2 text-xs font-medium text-neutral/60">
                  <div className="col-span-2">
                    {t.workOrderDetailView.completionDialog.itemColumn}
                  </div>
                  <div className="col-span-2">
                    {t.workOrderDetailView.completionDialog.targetLotColumn}
                  </div>
                  <div className="col-span-2">
                    {t.workOrderDetailView.completionDialog.targetTagColumn}
                  </div>
                  <div className="col-span-2">
                    {t.workOrderDetailView.completionDialog.expectedColumn}
                  </div>
                  <div className="col-span-2">
                    {t.workOrderDetailView.completionDialog.actualColumn}
                  </div>
                  <div className="col-span-2">
                    {t.workOrderDetailView.completionDialog.unitColumn}
                  </div>
                </div>
                {completionOutputs.map((item, index) => (
                  <div
                    key={item.outputItemId}
                    className="grid grid-cols-12 items-center gap-2 rounded border border-neutral/20 p-2"
                  >
                    <div className="col-span-2 truncate text-sm">
                      {item.itemName}
                    </div>
                    <div className="col-span-2">
                      <SelectInventoryLot
                        placeholder={
                          t.workOrderDetailView.completionDialog.createNewLot
                        }
                        value={item.targetLotId || undefined}
                        onChange={(lotId) =>
                          handleUpdateOutputLot(index, lotId)
                        }
                        facilityId={facilityId}
                        isDisabled={isSubmitting}
                      />
                    </div>
                    <div className="col-span-2">
                      <SelectInventoryTag
                        placeholder={
                          t.workOrderDetailView.completionDialog.selectTag
                        }
                        value={item.targetTagId || undefined}
                        onChange={(tagId) =>
                          handleUpdateOutputTag(index, tagId)
                        }
                        facilityId={facilityId}
                        itemId={item.itemId}
                        lotId={item.targetLotId || undefined}
                        isDisabled={isSubmitting}
                      />
                    </div>
                    <div className="col-span-2 text-sm text-neutral/60">
                      {item.expectedQuantity.toLocaleString()}{' '}
                      {item.expectedQuantityUnit}
                    </div>
                    <div className="col-span-2">
                      <NumberField
                        value={item.actualQuantity ?? undefined}
                        onChange={(val) =>
                          handleUpdateOutputActual(index, val ?? null)
                        }
                        minValue={0}
                        isDisabled={isSubmitting}
                        aria-label="Actual quantity"
                      />
                    </div>
                    <div className="col-span-2">
                      <Select
                        value={item.actualQuantityUnit}
                        onChange={(key) =>
                          handleUpdateOutputUnit(index, key as Unit)
                        }
                        options={unitOptions}
                        isDisabled={isSubmitting}
                        aria-label="Unit"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            color="neutral"
            onPress={handleClose}
            isDisabled={isSubmitting}
          >
            {t.workOrderDetailView.completionDialog.cancel}
          </Button>
          <Button
            icon={<Check />}
            color="success"
            onPress={handleSubmit}
            isDisabled={!canSubmit}
          >
            {isSubmitting
              ? t.workOrderDetailView.completionDialog.completing
              : t.workOrderDetailView.completionDialog.complete}
          </Button>
        </div>
      }
    />
  );
};
